import { Button, Flex, Text } from '@servicetitan/anvil2';
import { FormProvider, UseFormReturn, SubmitHandler } from 'react-hook-form';
import { TextField, SelectField } from '@servicetitan/anvil2-react-hook-form';
import { useEffect } from 'react';

interface GoodLeapFormProps {
    methods: UseFormReturn<{ amount: number; state?: string }>;
    handleSubmit: SubmitHandler<{ amount: number; state?: string }>;
    resetAmount: () => void;
    isSubmitDisabled: boolean;
    defaultAmount: number;
    defaultState?: string;
    availableStates: string[];
}

export const GoodLeapForm = ({
    methods,
    handleSubmit,
    resetAmount,
    isSubmitDisabled,
    defaultAmount,
    defaultState,
    availableStates,
}: GoodLeapFormProps) => {
    useEffect(() => {
        const currentState = methods.getValues('state');

        // If there's only one state available, auto-select it
        if (availableStates.length === 1) {
            const singleState = availableStates[0];
            if (currentState !== singleState) {
                methods.setValue('state', singleState, { shouldDirty: false });
            }
        } else if (availableStates.length > 1) {
            if (
                defaultState &&
                !availableStates.includes(defaultState) &&
                currentState === defaultState
            ) {
                methods.setValue('state', undefined, { shouldDirty: false });
            }
        }
    }, [availableStates, defaultState, methods]);
    const getCancelDisabled = () => {
        const { amount: currentAmount, state: currentState } = methods.getValues();
        const amountMatches = currentAmount === defaultAmount;

        const normalizedCurrentState = currentState?.trim().toUpperCase();
        const normalizedDefaultState = defaultState?.trim().toUpperCase();
        const stateMatches = normalizedCurrentState === normalizedDefaultState;

        return amountMatches && stateMatches;
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
                <Flex direction="column" gap="6">
                    <Flex direction="column">
                        <Text variant="headline" el="h2" style={{ margin: 0 }}>
                            Financing Information
                        </Text>
                        <Text>Enter your desired financed amount to view available plans.</Text>
                    </Flex>
                    <Flex
                        direction="row"
                        gap="6"
                        style={{
                            flexWrap: 'wrap',
                            alignItems: 'flex-start',
                            alignContent: 'flex-start',
                            padding: 0,
                        }}
                    >
                        <Flex
                            direction="column"
                            gap="2"
                            style={{
                                width: '295px',
                                minWidth: '295px',
                                flex: 'none',
                                flexGrow: 0,
                            }}
                        >
                            <TextField
                                label="Desired Financed Amount"
                                name="amount"
                                type="number"
                                prefix="$"
                                inputMode="decimal"
                            />
                            <Text style={{ marginTop: '4px', fontSize: '14px', color: '#606162' }}>
                                Amount can be updated later
                            </Text>
                        </Flex>
                        <div
                            style={{
                                width: '295px',
                                minWidth: '295px',
                                flex: 'none',
                                flexGrow: 0,
                            }}
                        >
                            <SelectField
                                name="state"
                                options={availableStates.map(state => ({
                                    id: state,
                                    label: state,
                                }))}
                                description="GoodLeap financing is only available in the listed states."
                                required
                                label="State"
                            />
                        </div>
                    </Flex>
                    <Flex direction="row" gap="2">
                        <Button appearance="primary" type="submit" disabled={isSubmitDisabled}>
                            Confirm
                        </Button>
                        <Button
                            appearance="secondary"
                            onClick={resetAmount}
                            disabled={getCancelDisabled()}
                        >
                            Cancel
                        </Button>
                    </Flex>
                </Flex>
            </form>
        </FormProvider>
    );
};
